import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { MatDialog } from '@angular/material/dialog';
import { Subscription, timer } from "rxjs";
import { IdentificationCheck } from "src/shared/providers/identification-check";
import { HtmlVideoRecorder } from "src/shared/providers/camera/html-video-recorder";
import { CommonUtils } from "src/shared/utils/common-utils";
import { InAppBrowserUtils } from "src/shared/utils/in-app-browser-utils";
import { tap } from "rxjs/operators";
import * as moment from 'moment';

const IDENTIFICATION_CHECK_STATUSES = {
  0: 'requires_input',
  1: 'processing',
  2: 'verified',
}

@Component({
  selector: 'identity-verification-modal',
  templateUrl: 'identity-verification.html',
  styleUrls: ['./identity-verification.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class IdentityVerificationModalComponent implements OnInit, OnDestroy {
  isMobileResolution = false;
  isOnIdentificationCheck: boolean;
  progressBars = [
    {
      mode: 'determinate',
      value: 100,
    },
    {
      mode: 'determinate',
      value: 0,
    },
    {
      mode: 'determinate',
      value: 0,
    },
  ];
  identificationSteps = [
    {
      icon: 'assets/icon/id.png',
      title: 'New clients require a quick identification check',
      subtitle: 'All you need is your drivers license.',
      displayButton: true,
      buttonText: 'Get Verified',
      action: () => this.getVerified(),
    },
    {
      icon: 'assets/icon/identify.png',
      title: 'Identification in progress',
      subtitle: 'This typically takes less than 1 minute.',
      displayButton: false,
      buttonText: '',
      action: () => {},
    },
    {
      icon: 'assets/icon/verify.png',
      title: 'Record a Quick Video',
      subtitle: '',
      displayButton: true,
      buttonText: 'Start Recording',
      action: () => this.startVideoRecording(),
    },
    {
      icon: 'assets/icon/verify.png',
      title: 'Verification Completed!',
      subtitle: 'Your identity has been successfully verified.',
      displayButton: true,
      buttonText: 'Ok',
      action: () => this.dismissModal(),
    }
  ];
  currentIdentificationStep = this.identificationSteps[0];
  verificationUrl = '';
  verificationSubscription = new Subscription();
  identificationError: any;
  selectedIdOption: string = '';
  isVideoStep = false;
  videoDeadline: moment.Moment;
  lastIdentificationCheck: any;
  loaded = false;

  constructor(
    private dialog: MatDialog,
    private modalCtrl: ModalController,
    private identificationCheck: IdentificationCheck,
    private htmlVideoRecorder: HtmlVideoRecorder,
    private utils: CommonUtils,
    private iabUtils: InAppBrowserUtils,
  ) {
    this.isMobileResolution = window.innerWidth <= 870;
  }

  async ngOnInit(): Promise<void> {
    this.loaded = false;
    await this.handleIdentificationCheck();
    this.loaded = true;
  }

  ngOnDestroy(): void {
    this.verificationSubscription?.unsubscribe();
  }

  async handleIdentificationCheck(): Promise<void> {
    this.resetProgressBar();
    const identificationCheck = await this.getIdentificationCheck();
    this.isOnIdentificationCheck = identificationCheck?.status !== IDENTIFICATION_CHECK_STATUSES[2];
    if (this.isOnIdentificationCheck) {
      this.currentIdentificationStep = (this.identificationError || identificationCheck?.status === IDENTIFICATION_CHECK_STATUSES[1]) ? this.identificationSteps[1] : this.identificationSteps[0];
      if (this.currentIdentificationStep === this.identificationSteps[1]) {
        this.progressBars[1] = {
          mode: 'indeterminate',
          value: 0,
        }
        this.startVerificationCheck();
      }
    } else {
      this.finishVerificationCheck();
    }
  }

  resetProgressBar(): void {
    this.progressBars[1] = {
      mode: 'determinate',
      value: 0,
    }
    this.progressBars[2] = {
      mode: 'determinate',
      value: 0,
    }
  }

  goToVerificationPage(): void {
    this.iabUtils.openUrl(this.verificationUrl);
  }

  getVerified(): void {
    this.startVerificationCheck();
    this.goToVerificationPage();
    this.currentIdentificationStep = this.identificationSteps[1];
    this.progressBars[1] = {
      mode: 'indeterminate',
      value: 0,
    }
  }

  async startVerificationCheck(): Promise<void> {
    const fifteenSeconds = 15000;
    this.verificationSubscription.add(timer(0, fifteenSeconds)
      .pipe(
        tap(async () => {
          const identificationCheck = await this.getIdentificationCheck()
          if (identificationCheck.status === IDENTIFICATION_CHECK_STATUSES[2]) {
            this.finishVerificationCheck();
            this.verificationSubscription.unsubscribe();
          }
        })
      )
      .subscribe());
  }

  finishVerificationCheck(): void {
    const check = this.lastIdentificationCheck;

    // If video not required (Didit handles liveness natively) or already uploaded, show success
    if (!check?.video_required || check?.video_url) {
      this.showFinalStep();
      return;
    }

    // Video needed (legacy Stripe checks) - show video step
    this.progressBars[1] = {
      mode: 'determinate',
      value: 100,
    };
    this.currentIdentificationStep = this.identificationSteps[2];
    this.isVideoStep = true;

    // Set deadline (show max 5 min even if more time, default to 5 min if not provided)
    const fiveMinFromNow = moment().add(5, 'minutes');
    if (check?.video_upload_deadline) {
      const deadline = moment(check.video_upload_deadline);
      this.videoDeadline = deadline.isBefore(fiveMinFromNow) ? deadline : fiveMinFromNow;
    } else {
      this.videoDeadline = fiveMinFromNow;
    }
  }

  async getIdentificationCheck(): Promise<any> {
    const identificationCheck = await this.identificationCheck.getIdentityCheck();
    this.lastIdentificationCheck = identificationCheck;
    this.verificationUrl = identificationCheck?.redirect_url;
    this.identificationError = identificationCheck?.last_error_message;
    console.log(identificationCheck);
    return identificationCheck;
  }

  onIdOptionChange(value: string): void {
    this.selectedIdOption = value;
  }

  async startVideoRecording(): Promise<void> {
    this.htmlVideoRecorder.openVideoRecorderModal().then(async (videoBlob) => {
      if (videoBlob) {
        this.uploadVideo(videoBlob).then(() => {});
      }
    }).catch((error) => {
      this.utils.showError('Video recording failed. Please try again.', 5000, false);
    });
  }

  async uploadVideo(videoBlob: Blob): Promise<void> {
    // Hide video instructions during upload
    this.isVideoStep = false;

    // Show uploading state
    this.currentIdentificationStep = {
      icon: 'assets/icon/identify.png',
      title: 'Uploading video...',
      subtitle: 'Please wait.',
      displayButton: false,
      buttonText: '',
      action: () => {},
    };
    this.progressBars[2] = {
      mode: 'indeterminate',
      value: 0,
    };

    try {
      const s3Response = await this.identificationCheck.uploadVideo(videoBlob);
      await this.identificationCheck.submitVideoUrl(s3Response.Location);
      this.showFinalStep();
    } catch (error) {
      this.utils.showError('Video upload failed. Please try again.', 5000, false);
      // Let user retry - show video step again with error
      this.currentIdentificationStep = this.identificationSteps[2];
      this.isVideoStep = true;
      this.progressBars[2] = {
        mode: 'determinate',
        value: 0,
      };
      const responseError = error?.error?.message || error?.message;
      const windowExpired = 'window has expired';
      if (responseError.includes(windowExpired)) {
        this.identificationError = { reason: `Video upload window has expired. Please restart the identification check.`}
        this.currentIdentificationStep = this.identificationSteps[0];
        return;
      }
      this.identificationError = { reason: `Video upload failed. Please try again. ${responseError}`}
    }
  }

  showFinalStep(): void {
    this.isVideoStep = false;
    this.identificationError = null;
    this.progressBars[1] = {
      mode: 'determinate',
      value: 100,
    };
    this.progressBars[2] = {
      mode: 'determinate',
      value: 100,
    };
    this.currentIdentificationStep = this.identificationSteps[3];
  }

  dismissModal(): void {
    this.dialog?.closeAll();
    this.modalCtrl.getTop().then((res) => {
      if (res) {
        this.modalCtrl.dismiss();
      }
    });
  }
}
